/**
 * Phase 9.4 — 🎁 First-Gift Nudge
 * --------------------------------
 * Renders below the credited-purchase confirmation card on /coins/return.
 * Suggests ONE high-signal target to spend the user's fresh coins on,
 * with a cascading fallback:
 *   1. A live beam happening right now      → /beams/{room_id}
 *   2. Hottest trending track                → /track/{submission_id}
 *   3. Current spotlight winner              → /track/{submission_id}
 *
 * If none of the three sources have data, the nudge silently hides — we
 * never show an empty card.
 *
 * Triggered ONLY after `phase === "credited"` on CoinReturnPage so the
 * wallet credit flow is never interrupted.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { getCoverUrl, getProfilePhotoUrl } from "@/lib/mediaUrls";

const FirstGiftNudge = () => {
  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch live + discover in parallel — both are cacheable public endpoints.
        const [liveRes, discoverRes] = await Promise.allSettled([
          api.get("/live/now?limit=1"),
          api.get("/discover/home"),
        ]);

        // 1. Live beam wins if there's anyone live right now.
        const liveRooms =
          liveRes.status === "fulfilled" ? liveRes.value.data?.rooms || [] : [];
        if (liveRooms.length > 0) {
          const room = liveRooms[0];
          if (!cancelled) {
            setSuggestion({
              kind: "live",
              title: room.title || "Live Beam",
              subtitle:
                `${room.host_name || "Hosted live"}`
                + (room.online_count ? ` · 👥 ${room.online_count} listening` : ""),
              imageUrl: getProfilePhotoUrl({
                profile_photo: room.host_profile_photo,
                user_id: room.host_user_id,
              }),
              link: `/beams/${room.room_id}`,
              ctaLabel: "🎁 Gift the Live Beam →",
              isLive: true,
              onlineCount: room.online_count || 0,
            });
            setLoading(false);
          }
          return;
        }

        const discover =
          discoverRes.status === "fulfilled" ? discoverRes.value.data || {} : {};

        // 2. Hottest trending track.
        const trending = discover.trending_now || [];
        if (trending.length > 0) {
          const t = trending[0];
          if (!cancelled) {
            setSuggestion({
              kind: "trending",
              title: t.track_title || t.title || "Trending track",
              subtitle: `🔥 Trending Now · ${t.artist_name || "Artist"}`,
              imageUrl: getCoverUrl(t),
              link: `/track/${t.submission_id}`,
              ctaLabel: "🎁 Gift this Track →",
            });
            setLoading(false);
          }
          return;
        }

        // 3. Spotlight winner fallback.
        const winners = discover.spotlight_winners?.winners || [];
        if (winners.length > 0) {
          const w = winners[0];
          if (!cancelled) {
            setSuggestion({
              kind: "spotlight",
              title: w.track_title || w.title || "Spotlight winner",
              subtitle: `🏆 Spotlight Winner · ${w.artist_name || "Featured artist"}`,
              imageUrl: getCoverUrl(w),
              link: `/track/${w.submission_id}`,
              ctaLabel: "🎁 Gift the Winner →",
            });
            setLoading(false);
          }
          return;
        }

        if (!cancelled) setLoading(false);
      } catch (_e) {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Hide entirely if nothing to suggest — never show an empty nudge.
  if (loading || !suggestion) return null;

  return (
    <Card
      className="mt-4 p-5 sm:p-6 bg-gradient-to-br from-pink-500/15 via-purple-500/10 to-amber-500/10 border-pink-400/40 shadow-lg shadow-pink-500/10"
      data-testid="first-gift-nudge"
    >
      <div className="flex items-start gap-2 mb-3">
        <span className="text-2xl shrink-0">🎁</span>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg sm:text-xl font-black text-white leading-tight">
            You've got coins — send your first gift?
          </h2>
          <p className="text-xs text-pink-200/80 mt-0.5">
            Drop some love on someone making moves right now.
          </p>
        </div>
      </div>

      <Link
        to={suggestion.link}
        className="flex items-center gap-3 p-3 bg-black/40 hover:bg-black/60 border border-pink-500/30 hover:border-pink-400/60 rounded-xl transition-all mb-4"
        data-testid={`first-gift-suggestion-${suggestion.kind}`}
      >
        <img
          src={suggestion.imageUrl}
          alt=""
          className="w-14 h-14 rounded-lg object-cover border border-pink-500/30 shrink-0"
        />
        <div className="min-w-0 flex-1 text-left">
          <p className="text-sm font-bold text-white truncate">
            {suggestion.title}
          </p>
          <p className="text-[11px] text-pink-300/80 truncate">
            {suggestion.subtitle}
          </p>
        </div>
        {suggestion.kind === "live" && (
          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-rose-500/20 text-rose-200 border border-rose-500/40 animate-pulse">
            ● Live
          </span>
        )}
      </Link>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Link to={suggestion.link} data-testid="first-gift-send-now" className="sm:col-span-1">
          <Button className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-400 hover:to-purple-500 text-white font-bold shadow-lg shadow-pink-500/30">
            🎁 Send Gift Now
          </Button>
        </Link>
        <Link to="/home" data-testid="first-gift-to-home" className="sm:col-span-1">
          <Button
            variant="outline"
            className="w-full border-pink-500/40 text-pink-200 hover:bg-pink-500/10"
          >
            🏠 Go to Home
          </Button>
        </Link>
        <Link to="/wallet" data-testid="first-gift-to-wallet" className="sm:col-span-1">
          <Button
            variant="outline"
            className="w-full border-amber-400/40 text-amber-200 hover:bg-amber-500/10"
          >
            💰 View Wallet
          </Button>
        </Link>
      </div>
    </Card>
  );
};

export default FirstGiftNudge;
